<template>
  <div class="min-h-screen bg-gradient-to-br from-dashboard-bg via-white to-dashboard-bg flex items-center justify-center p-4 sm:p-6">
    <div class="w-full max-w-md">
      <!-- Logo et titre -->
      <div class="text-center mb-6 sm:mb-8">
        <div class="flex justify-center mb-4 sm:mb-6">
          <img
            src="/logo.png"
            alt="Logo"
            class="h-16 sm:h-20 md:h-24 w-auto object-contain"
          />
        </div>
        <p class="text-dashboard-muted text-sm sm:text-base md:text-lg px-4">
          Gestion Électronique des Courriers Diplomatiques
        </p>
      </div>

      <!-- Formulaire de connexion -->
      <div class="bg-white border border-dashboard-border rounded-xl sm:rounded-2xl shadow-xl p-5 sm:p-6 md:p-8">
        <h2 class="text-xl sm:text-2xl font-bold text-dashboard-text mb-1 sm:mb-2 text-center">Connexion</h2>
        <p class="text-dashboard-muted text-sm sm:text-base mb-4 sm:mb-6 text-center">
          Accédez à votre espace de travail
        </p>

        <div
          v-if="error"
          class="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 sm:gap-3"
        >
          <AlertCircle class="w-4 h-4 sm:w-5 sm:h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p class="text-xs sm:text-sm text-red-800 break-words">{{ error }}</p>
        </div>

        <form @submit.prevent="handleSubmit" class="space-y-4 sm:space-y-6">
          <!-- Username -->
          <div>
            <label for="username" class="block text-xs sm:text-sm font-medium text-dashboard-text mb-1.5 sm:mb-2">
              Nom d'utilisateur
            </label>
            <div class="relative">
              <Mail class="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-dashboard-muted" />
              <input
                id="username"
                v-model="username"
                type="text"
                placeholder="votre username"
                class="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm sm:text-base text-dashboard-text placeholder:text-dashboard-muted focus:outline-none focus:border-dashboard-accent focus:ring-2 focus:ring-dashboard-accent/20 transition-all"
                required
              />
            </div>
          </div>

          <!-- Mot de passe -->
          <div>
            <label for="password" class="block text-xs sm:text-sm font-medium text-dashboard-text mb-1.5 sm:mb-2">
              Mot de passe
            </label>
            <div class="relative">
              <Lock class="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-dashboard-muted" />
              <input
                id="password"
                :type="showPassword ? 'text' : 'password'"
                v-model="password"
                placeholder="••••••••"
                class="w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-2.5 sm:py-3 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm sm:text-base text-dashboard-text placeholder:text-dashboard-muted focus:outline-none focus:border-dashboard-accent focus:ring-2 focus:ring-dashboard-accent/20 transition-all"
                required
              />
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-dashboard-muted hover:text-dashboard-text transition-colors"
              >
                <EyeOff v-if="showPassword" class="w-4 h-4 sm:w-5 sm:h-5" />
                <Eye v-else class="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>

          <!-- Options -->
          <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
            <label class="flex items-center gap-2 cursor-pointer group">
              <input
                type="checkbox"
                v-model="rememberMe"
                class="w-4 h-4 text-dashboard-accent border-dashboard-border rounded focus:ring-2 focus:ring-dashboard-accent/20 cursor-pointer"
              />
              <span class="text-xs sm:text-sm text-dashboard-text group-hover:text-dashboard-accent transition-colors whitespace-nowrap">
                Se souvenir de moi
              </span>
            </label>
            <button type="button" class="text-xs sm:text-sm text-dashboard-accent hover:underline font-medium whitespace-nowrap">
              Mot de passe oublié ?
            </button>
          </div>

          <!-- Bouton de connexion -->
          <button
            type="submit"
            :disabled="isLoading"
            class="w-full py-2.5 sm:py-3 bg-[#22282F] text-white rounded-lg text-sm sm:text-base font-medium hover:bg-[#22282F] focus:outline-none focus:ring-2 focus:ring-dashboard-accent/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <template v-if="isLoading">
              <div class="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span>Connexion en cours...</span>
            </template>
            <span v-else>Se connecter</span>
          </button>
        </form>

        <!-- Divider -->
        <div class="relative my-4 sm:my-6">
          <div class="absolute inset-0 flex items-center">
            <div class="w-full border-t border-dashboard-border"></div>
          </div>
          <div class="relative flex justify-center text-xs sm:text-sm">
            <span class="px-3 sm:px-4 bg-white text-dashboard-muted">Ou</span>
          </div>
        </div>

        <!-- Connexion alternative -->
        <button
          type="button"
          class="w-full py-2.5 sm:py-3 bg-white border-2 border-dashboard-border text-dashboard-text rounded-lg text-sm sm:text-base font-medium hover:bg-dashboard-bg transition-all flex items-center justify-center gap-2"
        >
          <svg class="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"
            />
          </svg>
          <span>Se connecter avec SSO</span>
        </button>
      </div>

      <!-- Footer -->
      <div class="mt-4 sm:mt-6 text-center px-4">
        <p class="text-xs sm:text-sm text-dashboard-muted">
          Vous n'avez pas de compte ?
          <button class="text-dashboard-accent hover:underline font-medium">Contactez le support</button>
        </p>
      </div>

      <!-- Version -->
      <div class="mt-6 sm:mt-8 text-center">
        <p class="text-xs text-dashboard-muted">v1.0 • © 2025 Ministère des Affaires Étrangères</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { Eye, EyeOff, Lock, Mail, AlertCircle } from 'lucide-vue-next'
import { useAuthStore } from '@/store/auth'

const router = useRouter()
const { login } = useAuthStore()

const username = ref('')
const password = ref('')
const showPassword = ref(false)
const rememberMe = ref(false)
const isLoading = ref(false)
const error = ref('')

const handleSubmit = async () => {
  error.value = ''
  isLoading.value = true

  try {
    const result = await login(username.value, password.value)

    switch (result.status) {
      case 'success':
        if (rememberMe.value) localStorage.setItem('rememberMe', 'true')
        router.push('/dashboard')
        break
      case 'otp_required':
        // 2FA active : saisir le code de l'application d'authentification
        router.push('/otp')
        break
      case 'setup_required':
        // 2FA obligatoire non configurée : afficher le QR code
        router.push('/setup-2fa')
        break
      default:
        error.value = result.error || 'Identifiants incorrects'
    }
  } catch (e) {
    error.value = 'Erreur de connexion au serveur'
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
</style>
